import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader, Mail } from 'lucide-react';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

export default function VerifyEmail() {
  const [status, setStatus] = useState('loading'); // loading | success | error | already
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');
  const email = new URLSearchParams(window.location.search).get('email');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('check'); // no token = just show "check your email" screen
    }
  }, []);

  const verifyEmail = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-email?token=${token}`);
      const data = await res.json();
      if (res.ok) {
        setStatus(data.alreadyVerified ? 'already' : 'success');
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  const resendVerification = async () => {
    if (!email) return;
    setResending(true);
    try {
      await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setResent(true);
    } catch (err) { }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-8 text-center">

          {/* Logo */}
          <button onClick={() => window.location.href = '/'} className="inline-block hover:opacity-80 transition-opacity mb-6">
            <img src="/logos/logo-color.png" alt="HIVE" className="h-10 w-auto mx-auto dark:hidden" />
            <img src="/logos/logo-white.png" alt="HIVE" className="h-10 w-auto mx-auto hidden dark:block" />
          </button>

          {/* Loading */}
          {status === 'loading' && (
            <div>
              <Loader className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Verifying your email...</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Just a moment!</p>
            </div>
          )}

          {/* Check your email */}
          {status === 'check' && (
            <div>
              <div className="w-16 h-16 bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-yellow-500" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">Check Your Email</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                We sent a verification link to <strong className="text-gray-900 dark:text-white">{email || 'your email'}</strong>. Click the link to activate your account.
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Didn't get it? Check your spam folder.</p>
              {email && !resent && (
                <button onClick={resendVerification} disabled={resending}
                  className="px-6 py-3 bg-yellow-400 text-white font-bold rounded-xl hover:bg-yellow-500 transition-all disabled:opacity-50 flex items-center gap-2 mx-auto">
                  {resending && <Loader className="w-4 h-4 animate-spin" />}
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}
              {resent && <p className="text-green-600 dark:text-green-400 text-sm font-semibold">✅ Verification email resent!</p>}
              <button onClick={() => window.location.href = '/auth'} className="mt-4 text-sm text-gray-500 hover:text-yellow-600 transition-colors block mx-auto">
                Back to Sign In
              </button>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div>
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">Email Verified! 🎉</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Your account is now active. You can sign in and start using HIVE!</p>
              <button onClick={() => window.location.href = '/auth'}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all">
                Sign In Now
              </button>
            </div>
          )}

          {/* Already verified */}
          {status === 'already' && (
            <div>
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">Already Verified</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Your email is already verified. Just sign in!</p>
              <button onClick={() => window.location.href = '/auth'}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all">
                Sign In
              </button>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div>
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">Link Expired</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">This verification link has expired or is invalid. Request a new one below.</p>
              {email && !resent && (
                <button onClick={resendVerification} disabled={resending}
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {resending && <Loader className="w-4 h-4 animate-spin" />}
                  {resending ? 'Sending...' : 'Send New Verification Email'}
                </button>
              )}
              {resent && <p className="text-green-600 dark:text-green-400 text-sm font-semibold">✅ New verification email sent!</p>}
              <button onClick={() => window.location.href = '/auth'} className="mt-4 text-sm text-gray-500 hover:text-yellow-600 transition-colors block mx-auto">
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}